<script lang="ts" setup>

import {getLastMessageDate} from "@src/helpers/lastMessageHelper";
import {whatsAppParser} from "@src/helpers/messageHelper";
import {resolveUrl, shorten} from "@src/helpers/utils";
import {computed} from "vue";
import useStore from "@src/stores/store";
import {useChatSessionStore} from "@src/stores/chatSession";
import {useMessageStore} from "@src/stores/messages";
import Contact from "@src/types/Contact";
import {useContacts} from "@src/composables/useContacts";


const props = defineProps([
    'id',
    'name',
    'type',
    'lastMessage',
    'lastMessageDate',
    'avatar',
    'status',
    'department',
    'hasNewMessage',
    'costumerId',
    "isFixed",
    "isSpin",
    'isExpired'
]);
const store = useStore()
const messageStore = useMessageStore()
const sessionStore = useChatSessionStore()
const contactsComposable = useContacts()
const contacts = contactsComposable.getAllContacts()

const hasContact = computed(() => {
    return contacts.value?.some((contact: Contact) => contact.costumer_id === props.costumerId)
})
const contact = computed(() => {
    return contacts.value?.find((contact: Contact) => contact.costumer_id === props.costumerId)
})

const lastMessageData = computed(() => props.lastMessage ?? null)

const lastMessage = computed(() => props.lastMessage?.message ?? '')

const lastMessageDate = computed(() => {
    if (props.lastMessageDate) {
        return getLastMessageDate(props.lastMessageDate)
    }
    return ''
})

const getIconUrl = (iconName: string) => {
    return new URL(`/src/assets/vectors/logo_${iconName}.svg`, import.meta.url).href;
}

const sessionClass = computed(() => {
  let base = 'flex items-center px-4 py-2 md:py-5 cursor-pointer md:rounded-sm transition-colors w-full border-b-gray-200 border-b-1 md:border-b-0 last:border-b-0'

  if (props.hasNewMessage) {
    return base + 'bg-green-100 text-white hover:bg-green-200 dark:bg-[#144d37] dark:hover:bg-[#195e42]'
  }

  if (store.settings.darkMode) {
    base += 'text-white '
    base += props.isExpired ? 'hover:bg-red-500 ' : 'hover:bg-gray-700 '
    if (sessionStore.selectedSession && sessionStore.selectedSession.id === props.id) {
      base += props.isExpired ? 'bg-red-500 ' : 'bg-gray-700 '
    } else {
      base += props.isExpired ? ' bg-red-400 ' : ''
    }
  } else {
    base += props.isExpired ? 'hover:bg-red-200 ' : 'hover:bg-gray-50 '
    if (sessionStore.selectedSession && sessionStore.selectedSession.id === props.id) {
      base += props.isExpired ? 'bg-red-200 ' : 'bg-gray-50 '
    } else {
      base += props.isExpired ? 'bg-red-100 ' : ''
    }
  }

  return base
})
</script>

<template>
    <div :class="sessionClass">
        <div v-if="sessionStore.openActionsBulk && (props.status === 'active' || props.status === 'hold')" class="flex items-center ml-[-10px] mr-3">
            <label class="ios-checkbox blue">
                <input
                    :checked="sessionStore.actionSessions.includes(props.id)"
                    type="checkbox"
                    @change="sessionStore.toggleActionSession(props.id)"
                />
                <div class="checkbox-wrapper">
                    <div class="checkbox-bg"></div>
                    <svg class="checkbox-icon" fill="none" viewBox="0 0 24 24">
                        <path
                            class="check-path"
                            d="M4 12L10 18L20 6"
                            stroke="currentColor"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="3"
                        ></path>
                    </svg>
                </div>
            </label>
        </div>

        <div class="relative flex-shrink-0">
            <div
                :style="{ backgroundImage: `url(${resolveUrl(props.avatar)})` }"
                class="w-8 h-8 rounded-full bg-cover bg-center"
            ></div>

            <img
                :src="getIconUrl(props.type)"
                alt="Tipo do Canal"
                class="absolute -bottom-1 -right-1 h-5 w-5 rounded-full ring-2 ring-white bg-white"
            />
        </div>

        <div class="flex flex-col w-full ml-3 h-[70px]">
            <!-- primeira linha-->
            <div class="w-full flex w-full justify-between">
              <!-- Nome -->
              <h3 class="text-sm font-semibold text-slate-800 truncate dark:text-white flex items-center gap-1">
                <span class="truncate">
                    {{ shorten(hasContact ? contact?.name : props.name, 23) }}
                </span>
              </h3>

              <!-- Data -->
              <span class="text-xs text-gray-400 whitespace-nowrap ml-2 dark:text-white">
                {{ lastMessageDate }}
              </span>
            </div>

            <!-- segunda linha-->
            <div class="w-full flex w-full justify-between">

                <!--last message-->
                <div v-if="lastMessageData && lastMessageData.type === 'text' && lastMessageData.status !== '5'"
                     class="text-sm text-gray-500 truncate pr-2 dark:text-gray-300"
                     v-html="whatsAppParser(shorten(lastMessage, 25))">
                </div>
                <div v-else-if="lastMessageData && lastMessageData.status === '5'"
                     class="text-sm text-gray-500 truncate pr-2 dark:text-gray-300">Mensagem apagada
                </div>
                <div v-else class="text-sm text-gray-500 truncate pr-2 dark:text-gray-300">
                  {{ messageStore.getLastMessagePreview(props.lastMessage) }}
                </div>

              <!-- badge new messages-->
              <div v-if="hasNewMessage"
                  class="w-[1.1rem] h-[1.1rem] rounded-full bg-green-400 animate-pulse">
              </div>
            </div>

            <!-- terceira linha-->
            <div class="w-full flex w-full justify-between mt-2">
                  <div class="flex items-center">
                      <!-- setor-->
                      <span v-if="props.department" class="text-[10px] font-medium rounded-full bg-brand-100 text-indigo-800 py-0.5 px-2.5 border border-indigo-200 ">
                          {{(shorten(props.department, 30))}}
                      </span>
                      <!-- roleta de leads-->
                      <span v-if="props.isSpin" class="inline-flex ml-1 align-middle">
                          <svg :fill="store.settings.darkMode ? 'white' : '#898989'" class="ml-1" height="16px" viewBox="0 0 32 32"
                               width="16px"
                               xmlns="http://www.w3.org/2000/svg">
                              <g id="_x30_3">
                                  <path
                                      d="m29.7070313 16.2929688-2-2c-.390625-.390625-1.0234375-.390625-1.4140625 0l-2 2c-.390625.390625-.390625 1.0234375 0 1.4140625s1.0234375.390625 1.4140625 0l.2929687-.2929688v.5859375c0 5.5141602-4.4863281 10-10 10-.5522461 0-1 .4477539-1 1s.4477539 1 1 1c6.6171875 0 12-5.3833008 12-12v-.5859375l.2929688.2929688c.1953125.1953125.4511718.2929687.7070312.2929687s.5117188-.0976563.7070313-.2929688c.390625-.3906249.390625-1.0234374 0-1.4140624z"/>
                                  <path
                                      d="m16 4c.5522461 0 1-.4477539 1-1s-.4477539-1-1-1c-6.6166992 0-12 5.3833008-12 12v.5859375l-.2929688-.2929688c-.390625-.390625-1.0234375-.390625-1.4140625 0s-.390625 1.0234375 0 1.4140625l2 2c.1953126.1953126.4511719.2929688.7070313.2929688s.5117188-.0976563.7070313-.2929688l2-2c.390625-.390625.390625-1.0234375 0-1.4140625s-1.0234375-.390625-1.4140625 0l-.2929688.2929688v-.5859375c0-5.5141602 4.4858398-10 10-10z"/>
                                  <circle cx="16" cy="13" r="3"/>
                                  <path
                                      d="m21 21v-2c0-1.6542969-1.3457031-3-3-3h-2-2c-1.6542969 0-3 1.3457031-3 3v2c0 .5522461.4472656 1 1 1h8c.5527344 0 1-.4477539 1-1z"/>
                              </g>
                          </svg>
                       </span>
                  </div>

                  <!-- fixar-->
                  <span v-if="props.isFixed" class="inline-flex ml-1 align-middle">
                        <svg :fill="store.settings.darkMode ? 'white' : '#898989'"  class="h-3.5 w-3.5 rotate-45"  viewBox="0 -960 960 960"
                             xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M648-503.54 733.54-420v86H523v250l-43 42-43-42v-250H226.46v-86L312-503.54V-788h-42v-86h420v86h-42v284.46Z"/>
                        </svg>
                  </span>
            </div>
        </div>
    </div>
</template>

<style scoped>
.checkbox-container {
    display: flex;
    gap: 20px;
    padding: 20px;
    background: #f8fafc;
    border-radius: 16px;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -2px rgba(0, 0, 0, 0.05);
}

.ios-checkbox {
    --checkbox-size: 22px;
    --checkbox-color: #3b82f6;
    --checkbox-bg: #dbeafe;
    --checkbox-border: #93c5fd;

    position: relative;
    display: inline-block;
    cursor: pointer;
    user-select: none;
    -webkit-tap-highlight-color: transparent;
}

.ios-checkbox input {
    display: none;
}

.checkbox-wrapper {
    position: relative;
    width: var(--checkbox-size);
    height: var(--checkbox-size);
    border-radius: 8px;
    transition: transform 0.2s ease;
}

.checkbox-bg {
    position: absolute;
    inset: 0;
    border-radius: 8px;
    border: 2px solid var(--checkbox-border);
    transition: all 0.2s ease;
}

.checkbox-icon {
    position: absolute;
    inset: 0;
    margin: auto;
    width: 80%;
    height: 80%;
    color: white;
    transform: scale(0);
    transition: all 0.2s ease;
}

.check-path {
    stroke-dasharray: 40;
    stroke-dashoffset: 40;
    transition: stroke-dashoffset 0.3s ease 0.1s;
}

/* Checked State */
.ios-checkbox input:checked + .checkbox-wrapper .checkbox-bg {
    background: var(--checkbox-color);
    border-color: var(--checkbox-color);
}

.ios-checkbox input:checked + .checkbox-wrapper .checkbox-icon {
    transform: scale(1);
}

.ios-checkbox input:checked + .checkbox-wrapper .check-path {
    stroke-dashoffset: 0;
}

/* Hover Effects */
.ios-checkbox:hover .checkbox-wrapper {
    transform: scale(1.05);
}

/* Active Animation */
.ios-checkbox:active .checkbox-wrapper {
    transform: scale(0.95);
}

/* Focus Styles */
.ios-checkbox input:focus + .checkbox-wrapper .checkbox-bg {
    box-shadow: 0 0 0 4px var(--checkbox-bg);
}

/* Color Themes */
.ios-checkbox.blue {
    --checkbox-color: #7C8CF5;
    --checkbox-bg: #dbeafe;
    --checkbox-border: #7C8CF5;
}

/* Animation */
@keyframes bounce {
    0%,
    100% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.1);
    }
}

.ios-checkbox input:checked + .checkbox-wrapper {
    animation: bounce 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

</style>